import { useParams } from "react-router-dom";

/**
 * useCostBreakdownParams - Hook for parsing and resolving route parameters for cost breakdown
 * 
 * SUMMARY:
 * Extracts and resolves projectId, costType, taskId, and stageId from route parameters.
 * Handles multiple ways of getting projectId (props, withRouter params, useParams, URL pathname).
 * Determines if the breakdown is stage-level or task-level based on route structure.
 * 
 * RESPONSIBILITIES:
 * - Resolve projectId from multiple possible sources
 * - Parse costType from route params
 * - Determine if breakdown is stage-level or task-level
 * - Extract final stageId and taskId values
 * 
 * @param {Object} props - Component props that may contain projectId or params
 * @returns {Object} Parsed route parameters and flags
 * @returns {string|null} projectId - Resolved project ID
 * @returns {string|null} costType - Cost type from route (material, labor, subcontractor, all)
 * @returns {string|null} taskId - Task ID if task-level breakdown
 * @returns {string|null} finalStageId - Stage ID if stage-level breakdown
 * @returns {boolean} isStageBreakdown - Whether this is a stage-level breakdown
 */
export const useCostBreakdownParams = (props) => {
  const params = useParams();

  // Try multiple ways to get projectId: from props, from props.params (withRouter), from useParams, or from location pathname
  const projectId = props.projectId || props.params?.projectId || params.projectId || (() => {
    // Fallback: extract from URL path if available
    const match = window.location.pathname.match(/\/budget\/([^/]+)\//);
    return match ? match[1] : null;
  })();

  const costType = params.costType;
  const taskIdParam = params.taskId;

  // Check if we have stageId directly from route params (new route: cost-breakdown/:costType/stage/:stageId)
  // or from taskId (old route format: cost-breakdown/:costType/:taskId where taskId = "stage/{stageId}")
  const isStageBreakdown = !!params.stageId || (taskIdParam && taskIdParam.startsWith('stage/'));
  const finalStageId = params.stageId || (isStageBreakdown && taskIdParam ? taskIdParam.replace('stage/', '') : null);
  const taskId = isStageBreakdown ? null : taskIdParam;

  return {
    projectId,
    costType,
    taskId,
    finalStageId,
    isStageBreakdown,
    taskIdParam // Keep for error messages
  };
};
